import React, { useState, useRef, useEffect, forwardRef } from 'react';
import { DayPicker } from 'react-day-picker';
import { format, isValid, parseISO, startOfToday, isToday, parse } from 'date-fns';
import classNames from 'classnames';
import { DATE_FORMATS, TIME_FORMATS, TIME_PATTERNS } from 'constants/date';
import { getDateTimePatterns } from 'utils/date';
import { SkeletonField } from 'components/layout/loading';
import TimeField from '../TimeField/TimeField';
import InputLabel from '../InputLabel/InputLabel';
import InputError from '../InputError/InputError';
import 'react-day-picker/style.css';
import './DateField.css';

const DateField = forwardRef(({
  label,
  value,
  onChange,
  onBlur,
  error,
  placeholder = 'Select date',
  className = 'w-full',
  loading = false,
  name,
  showTime = false,
  hideYear = false,
  disablePast = false,
  dateFormat = DATE_FORMATS.MM_DD_YYYY,
  timeFormat = TIME_FORMATS.H24,
}, ref) => {
  const [isOpen, setIsOpen] = useState(false);
  const [timeInput, setTimeInput] = useState('');
  const containerRef = useRef(null);
  const buttonRef = useRef(null);
  const { datePattern, timePattern } = getDateTimePatterns({ dateFormat, timeFormat });

  // Combine refs
  useEffect(() => {
    if (ref) {
      if (typeof ref === 'function') {
        ref(buttonRef.current);
      } else {
        ref.current = buttonRef.current;
      }
    }
  }, [ref]);

  // Sync timeInput with value
  const valueAsDate = value ? (typeof value === 'string' ? parseISO(value) : value) : null;
  const valueTimeStr = isValid(valueAsDate) ? format(valueAsDate, timePattern) : '';

  useEffect(() => {
    setTimeInput(valueTimeStr);
  }, [valueTimeStr]);

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleDaySelect = (date) => {
    if (!date) {
      onChange(null);
      return;
    }

    let newDate = date;
    const currentValue = value ? (typeof value === 'string' ? parseISO(value) : value) : null;

    if (isValid(currentValue)) {
      newDate.setHours(currentValue.getHours());
      newDate.setMinutes(currentValue.getMinutes());
    } else {
      newDate.setHours(0, 0, 0, 0);
    }

    onChange(format(newDate, "yyyy-MM-dd'T'HH:mm"));
    if (!showTime) {
      setIsOpen(false);
    }
  };

  const handleTimeChange = (newTime) => {
    setTimeInput(newTime);

    let hours, minutes;
    let isValidTime = false;

    if (timeFormat === TIME_FORMATS.H12) {
      const parsed = parse(newTime, TIME_PATTERNS.H12, new Date());
      if (isValid(parsed)) {
        hours = parsed.getHours();
        minutes = parsed.getMinutes();
        isValidTime = true;
      }
    } else {
      if (/^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/.test(newTime)) {
        [hours, minutes] = newTime.split(':').map(Number);
        isValidTime = true;
      }
    }

    if (isValidTime) {
      const currentValue = value
        ? (typeof value === 'string' ? parseISO(value) : value)
        : new Date();
      const newDate = isValid(currentValue) ? new Date(currentValue) : new Date();
      newDate.setHours(hours);
      newDate.setMinutes(minutes);

      onChange(format(newDate, "yyyy-MM-dd'T'HH:mm"));
    }
  };

  if (loading) {
    return <SkeletonField className={className} />;
  }

  const dateValue = value
    ? (typeof value === 'string' ? parseISO(value) : value)
    : null;
  const isValidDate = isValid(dateValue);

  const buttonClassNames = classNames(
    "relative w-full cursor-default rounded-md bg-white/5 py-1.5 pl-3 pr-10 text-left text-white shadow-sm ring-1 ring-inset ring-white/10 focus:outline-none focus:ring-2 focus:ring-indigo-500 sm:text-sm sm:leading-6",
    error && "ring-red-500 focus:ring-red-500"
  );

  const getDisplayFormat = () => {
    let displayDatePattern = datePattern;

    if (hideYear) {
      displayDatePattern = 'MMM d';
    }

    if (showTime) {
      return `${displayDatePattern} ${timePattern}`;
    }
    return displayDatePattern;
  };

  return (
    <div className={className} ref={containerRef}>
      {label && (
        <InputLabel htmlFor={name} className="mb-2">
          {label}
        </InputLabel>
      )}
      <div className="relative mt-2">
        <button
          ref={buttonRef}
          type="button"
          id={name}
          onClick={() => setIsOpen(!isOpen)}
          onBlur={onBlur}
          className={buttonClassNames}
        >
          <span className={classNames("block truncate", !isValidDate && "text-gray-400")}>
            {isValidDate ? format(dateValue, getDisplayFormat()) : placeholder}
          </span>
          <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
            <svg className="h-5 w-5 text-gray-400" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
              <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
            </svg>
          </span>
        </button>

        {isOpen && (
          <div className="absolute z-10 mt-1 w-auto rounded-md bg-gray-800 p-3 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
            <DayPicker
              mode="single"
              selected={dateValue}
              onSelect={handleDaySelect}
              showOutsideDays
              disabled={disablePast ? { before: startOfToday() } : undefined}
              formatters={hideYear ? {
                formatCaption: (date, options) => format(date, 'MMMM', { locale: options?.locale })
              } : undefined}
            />
            {showTime && (
              <div className="mt-3 border-t border-gray-700 pt-3">
                <TimeField
                  label="Time"
                  value={timeInput}
                  onChange={handleTimeChange}
                  name={`${name}-time`}
                  minTime={disablePast && dateValue && isToday(dateValue) ? format(new Date(), 'HH:mm') : undefined}
                  use12HourFormat={timeFormat === TIME_FORMATS.H12}
                />
              </div>
            )}
          </div>
        )}
      </div>
      {error && <InputError className="mt-2">{error}</InputError>}
    </div>
  );
});

export default DateField;
